import React from 'react';

// Styles & Assets
import styles from './TablePharmacy.module.css';
import './../../../styles/Pharmacy.css';

// Services
import { computeColsPercentage } from '../../../services/Pharmacy';

// Components
import RowPharmacy from '../RowPharmacy/RowPharmacy';
import HeaderPharmacy from '../HeaderPharmacy/HeaderPharmacy';
import DayAdminDialog from '../DayAdminDialog/DayAdminDialog';
import ContentCells from '../ContentCells/ContentCells';

// interfaces
import { DrugAdministrationList } from '../../../interfaces/Drug';

// Language
import { useTranslation } from 'react-i18next';

// Hooks
import { useEffect, useRef } from 'react';

const TablePharmacy: React.FC<{
    mandatoryDrugs: DrugAdministrationList[]
    optionalDrugs: DrugAdministrationList[]
    dates: string[]
    readOnly: boolean
    handleSetDrugs: (updateDrugs: (oldDrugs: DrugAdministrationList[]) => DrugAdministrationList[]) => void
    handleSetOptionalDrugs: (updateDrugs: (oldDrugs: DrugAdministrationList[]) => DrugAdministrationList[]) => void
}> = ({ mandatoryDrugs, optionalDrugs, dates, readOnly, handleSetDrugs, handleSetOptionalDrugs }) => {

    const { t } = useTranslation();

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const datesPercentages = computeColsPercentage(mandatoryDrugs, dates);

    useEffect(() => {
        if (mandatoryDrugs.length === 0 && optionalDrugs.length === 0) return;

        const container = tableContainerRef.current;
        if (!container) return;

        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const today = `${year}-${month}-${day}`;

        const scrollToToday = () => {
            const target = container.querySelector(`[data-date="${today}"]`) as HTMLElement | null;
            if (!target) return;

            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const stickyOffset = 301;
            const nextScrollLeft = container.scrollLeft + (targetRect.left - containerRect.left) - stickyOffset;

            container.scrollTo({
                left: Math.max(0, nextScrollLeft),
                behavior: 'auto'
            });
        };

        const frame = window.requestAnimationFrame(scrollToToday);

        return () => {
            window.cancelAnimationFrame(frame);
        };
    }, [dates, mandatoryDrugs.length, optionalDrugs.length]);

    return (
        <div ref={tableContainerRef} className={styles.tableContainer}>
            {(mandatoryDrugs.length !== 0 || optionalDrugs.length !== 0) ?
                <table className={styles.tablePharmacy}>
                    <HeaderPharmacy
                        datesPercentages={datesPercentages}
                        dates={dates}
                        readOnly={readOnly}
                    >
                        <DayAdminDialog
                            drugs={[...mandatoryDrugs, ...optionalDrugs]}
                            handleSetDrugs={handleSetDrugs}
                            handleSetOptionalDrugs={handleSetOptionalDrugs}
                            date={''}
                            showDialog={() => undefined}
                        />
                    </HeaderPharmacy>
                    <tbody>
                        {mandatoryDrugs.map(drug =>
                            <RowPharmacy
                                key={drug.drugId}
                                drug={drug}
                            >
                                <ContentCells
                                    drug={drug}
                                    dates={dates}
                                    handleSetDrugs={handleSetDrugs}
                                    readOnly={readOnly}
                                />
                            </RowPharmacy>
                        )}

                        <tr className={optionalDrugs.length > 0 ? styles.space : ''}></tr>
                        {optionalDrugs.map(drug =>
                            <RowPharmacy
                                key={drug.drugId}
                                drug={drug}
                                optionalTable
                            >
                                <ContentCells
                                    drug={drug}
                                    dates={dates}
                                    readOnly={readOnly}
                                    handleSetDrugs={handleSetOptionalDrugs}
                                />
                            </RowPharmacy>
                        )}
                    </tbody>
                </table>
                :
                <div className={styles.noData}>
                    {t('no_data')}
                </div>
            }
        </div>
    );
};

export default TablePharmacy;